import { Button, IconButton, Text } from '@components'
import { Circle, X } from 'lucide-solid'
import { type Accessor, type Setter, Show, createSignal, onCleanup } from 'solid-js'
import { css } from 'styled-system/css'
import { Flex, Grid } from 'styled-system/jsx'

export default function (props: {
	isOpen: Accessor<boolean>
	setIsOpen: Setter<boolean>
	setAudio: (url: string) => void
}) {
	const [recording, setRecording] = createSignal(false)
	const [audioURL, setAudioURL] = createSignal<string | null>(null)
	let mediaRecorder: MediaRecorder | null = null
	let audioChunks: Blob[] = []

	const startRecording = async () => {
		const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
		mediaRecorder = new MediaRecorder(stream)

		mediaRecorder.ondataavailable = (event) => {
			audioChunks.push(event.data)
		}

		mediaRecorder.onstop = () => {
			const blob = new Blob(audioChunks, { type: 'audio/webm' })
			const url = URL.createObjectURL(blob)
			setAudioURL(url)
			audioChunks = []
		}

		mediaRecorder.start()
		setRecording(true)
	}

	const stopRecording = () => {
		mediaRecorder?.stop()
		setRecording(false)
	}

	const confirmAudioAttachment = () => {
		const audio = audioURL()
		if (audio) {
			props.setAudio(audio)
			setAudioURL(null)
		}
	}

	onCleanup(() => {
		if (mediaRecorder?.state === 'recording') {
			mediaRecorder.stop()
		}
	})

	return (
		<Show when={props.isOpen()}>
			<IconButton
				class={css({
					position: 'absolute',
					top: '2',
					right: '2',
					zIndex: 2,
				})}
				onClick={() => props.setIsOpen(false)}
				variant="subtle"
				colorPalette="tscGreen"
				size="xs"
			>
				<X />
			</IconButton>
			<Grid
				p={8}
				gap={4}
				class={css({
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					height: '100%',
					background: 'bg.default',
					zIndex: 1,
					alignContent: 'center',
					justifyContent: 'center',
				})}
			>
				<Button
					colorPalette="tscGreen"
					variant="outline"
					onClick={recording() ? stopRecording : startRecording}
				>
					{recording() ? (
						<>
							<Circle fill="var(--colors-fg-error)" />
							<Text children="Stop Recording" />
						</>
					) : audioURL() ? (
						'Restart Recording'
					) : (
						'Start Recording'
					)}
				</Button>

				<Show when={audioURL() && !recording()}>
					<Flex gap={4}>
						<audio
							class={css({
								height: '40px',
							})}
							controls
							src={audioURL()!}
						/>
						<Button
							onClick={confirmAudioAttachment}
							colorPalette="tscGreen"
							variant="solid"
						>
							Confirm
						</Button>
					</Flex>
				</Show>
			</Grid>
		</Show>
	)
}
